import { ReactNode, useMemo, useRef, useState } from 'react';
import {
  NativeSyntheticEvent,
  TextInput,
  TextInputChangeEventData,
  TextInputFocusEventData,
} from 'react-native';
import { BodyText } from '@/common/components/BodyText/BodyText';
import {
  FloatingField,
  IFloatingFieldProps,
  LabeledInput,
} from '@/common/components/Fields/FloatingField/FloatingField';
import { Icon } from '@/common/components/Icons/Icon';
import { Label } from '@/common/components/Label/Label';
import { Stack } from '@/common/components/Stack/Stack';
import {
  MoneyUtils,
  TMoney,
  defaultMoney,
} from '@coverflex-tech/hypernova-utils';
import { styles } from './MoneyField.styles';

export const MoneyField = ({
  id,
  label,
  amount,
  availableAmount,
  availableLabel,
  error,
  disabled,
  onChange,
  onBlur,
  className,
  rightSibling,
  inline,
  hint,
  currencySymbol,
  size = 'md',
}: IMoneyFieldProps) => {
  const [editingValue, setEditingValue] = useState<string>(
    amount ? `${MoneyUtils.moneyToFloat(amount)}` : ''
  );
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<TextInput | null>(null);

  const handleChange = (
    evt: NativeSyntheticEvent<TextInputChangeEventData>
  ) => {
    const { text } = evt.nativeEvent;
    const sanitizedText = text
      .replace(/[^\d.,]/g, '')
      .replace(/[.,]/g, ',')
      .replace(/(,.*),/g, '$1')
      .replace(/(,\d{2})\d+$/, '$1');

    setEditingValue(sanitizedText);
    onChange(
      sanitizedText.length > 0
        ? MoneyUtils.parseString(
            sanitizedText,
            amount ? amount.currency : defaultMoney.currency
          )
        : null
    );
  };

  const formattedValue = useMemo(() => {
    if (amount === undefined || amount === null) {
      return '';
    }

    return focused ? editingValue : MoneyUtils.moneyToString(amount);
  }, [amount, focused, editingValue]);

  const icon = useMemo(() => {
    if (error) {
      return <Icon icon="circle-exclamation" color="systemError" />;
    }
  }, [error]);

  const hasRightContent = !!icon || !!rightSibling || !inline;
  const hasAvailableAmount = !!availableAmount && !!availableLabel && !inline;
  const showCurrencySymbol = !formattedValue || focused;

  return (
    <FloatingField
      error={error}
      focused={focused}
      disabled={disabled}
      className={className}
      wrapperClassName={!inline ? styles.root({ size }) : undefined}
      hint={hint}
      onPress={() => inputRef.current?.focus()}
      inline={inline}
    >
      <LabeledInput
        label={label}
        ref={inputRef}
        inline={inline}
        horizontalAlign={inline ? 'right' : 'left'}
        active={focused || !!amount}
        inputProps={{
          id,
          keyboardType: 'numeric',
          className: !inline ? styles.input({ size }) : undefined,
          value: formattedValue,
          onChange: handleChange,
          onFocus: () => {
            setFocused(true);
          },
          onBlur: (evt) => {
            setFocused(false);
            setEditingValue(formattedValue);
            onBlur?.(evt);
          },
          disabled,
          'aria-invalid': !!error,
        }}
      />
      {inline && showCurrencySymbol && (
        <Label bold color={disabled ? 'neutral20' : 'neutral30'}>
          {currencySymbol}
        </Label>
      )}
      {hasRightContent && (
        <Stack horizontal align="center" gap="sm" className="pl-sm">
          {rightSibling
            ? rightSibling
            : hasAvailableAmount && (
                <Stack align="end">
                  <BodyText color="neutral20" size="sm">
                    {availableLabel}
                  </BodyText>
                  <BodyText color="neutral20" size="sm" bold>
                    {MoneyUtils.moneyToString(availableAmount)}
                  </BodyText>
                </Stack>
              )}
          {icon}
        </Stack>
      )}
    </FloatingField>
  );
};

type FloatingFieldExtendable = Pick<
  IFloatingFieldProps,
  'hint' | 'error' | 'disabled'
>;

export interface IMoneyFieldProps extends FloatingFieldExtendable {
  id: string;
  label: string;
  amount?: TMoney | null;
  availableAmount?: TMoney;
  availableLabel?: string;
  onChange: (amount: TMoney | null) => void;
  onBlur?: (evt: NativeSyntheticEvent<TextInputFocusEventData>) => void;
  className?: string;
  rightSibling?: ReactNode;
  inline?: boolean;
  currencySymbol?: ReactNode;
  size?: 'sm' | 'md';
  step?: number;
}
